import React, { useEffect, useState, PropsWithChildren } from 'react';
import {
  Grid,
  Paper,
  Typography,
  Rating,
  Box,
  Stack,
  Card,
  CardContent,
  Alert,
} from '@mui/material';
import GlobalStyles from '@mui/material/GlobalStyles';
import { GoogleMap, Marker } from '@react-google-maps/api';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import { useGoogleMaps } from '@/context/GoogleMapsProvider.tsx';
import { formatCurrency, formatDecimal, formatPercentage } from '@/utils/misc.ts';
import { OpportunityFormData } from '@/features/opportunities/form/types.ts';
import { TernaryOption } from '@inversor-hub/types';
import { useGetOpportunityMedia } from '@/lib/api/opportunities.ts';
import { getTernaryLabel } from '@/utils/ternary-enum.ts';
import ImageGallery from '@/features/opportunities/form/ImageGallery.tsx';
import { investmentTypeLabels } from '@/features/investments/constants.ts';
import RichTextDisplay from '@/components/ui/RichTextDisplay.tsx';
import { calculateTransactionResults } from '@/features/opportunities/form/utils.ts';

interface OpportunityPreviewProps {
  data: OpportunityFormData;
  opportunityId: string;
  mainImageId?: string;
}

const MAP_ZOOM = 15;

const getTernaryOptionIcon = (value: TernaryOption) =>
  value === 'YES' ? <CheckCircleOutlineIcon color="success" /> : <HighlightOffIcon color="error" />;

const getBackgroundColor = (index: number) => (index % 2 === 0 ? '#f7f7f7' : '#ffffff');

const NoSaveZone: React.FC<PropsWithChildren<{ selector?: string }>> = ({ children, selector }) => {
  const scope = selector ?? '.no-save-zone';
  return (
    <>
      <GlobalStyles
        styles={{
          [scope]: {
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
          },
          [`${scope}::-webkit-scrollbar`]: {
            display: 'none',
          },
          [`${scope} img`]: {
            userSelect: 'none',
            WebkitUserSelect: 'none',
            WebkitUserDrag: 'none',
            pointerEvents: 'auto',
          },
        }}
      />
      <div
        className={scope.replace('.', '')}
        onContextMenu={(e) => e.preventDefault()}
        onDragStart={(e) => e.preventDefault()}
        onMouseDown={(e) => {
          if ((e.target as HTMLElement).tagName.toLowerCase() === 'img') e.preventDefault();
        }}
      >
        {children}
      </div>
    </>
  );
};

const RoomSaleAlert: React.FC = () => (
  <Alert severity="info" sx={{ mb: 2 }}>
    <Typography variant="body2">
      <strong>Información importante:</strong> En esta oportunidad estás comprando un porcentaje del
      inmueble. El funcionamiento es el mismo que al comprar una vivienda completa, pero en este
      caso estás adquiriendo una parte: tu habitación y un porcentaje de las zonas comunes.
    </Typography>
  </Alert>
);

const ReturnMetricsCards: React.FC<{
  grossReturn: number;
  netReturn: number;
  isFlipping?: boolean;
}> = ({ grossReturn, netReturn, isFlipping = false }) => (
  <Grid container spacing={2}>
    <Grid item xs={12} sm={6}>
      <Card
        elevation={0}
        sx={{
          border: '2px solid',
          borderColor: 'primary.main',
          borderRadius: 1.5,
          backgroundColor: 'primary.50',
          height: '100%',
        }}
      >
        <CardContent sx={{ p: 2.5 }}>
          <Stack spacing={1.5}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <ShowChartIcon sx={{ fontSize: 20, color: 'primary.main' }} />
              <Typography
                variant="body2"
                fontWeight="600"
                sx={{
                  color: 'text.secondary',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  fontSize: '0.75rem',
                }}
              >
                Rentabilidad Bruta
              </Typography>
            </Stack>
            <Typography
              variant="h4"
              fontWeight="700"
              sx={{
                color: 'primary.main',
                fontFamily: 'monospace',
                letterSpacing: '-0.5px',
              }}
            >
              {formatPercentage(grossReturn)}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.4 }}>
              {isFlipping ? 'Solo incluye precio compra y reforma' : 'Rentabilidad sin gastos'}
            </Typography>
          </Stack>
        </CardContent>
      </Card>
    </Grid>
    <Grid item xs={12} sm={6}>
      <Card
        elevation={0}
        sx={{
          border: '2px solid',
          borderColor: 'success.main',
          borderRadius: 1.5,
          backgroundColor: 'success.50',
          height: '100%',
        }}
      >
        <CardContent sx={{ p: 2.5 }}>
          <Stack spacing={1.5}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <TrendingUpIcon sx={{ fontSize: 20, color: 'success.main' }} />
              <Typography
                variant="body2"
                fontWeight="600"
                sx={{
                  color: 'text.secondary',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  fontSize: '0.75rem',
                }}
              >
                Rentabilidad Neta
              </Typography>
            </Stack>
            <Typography
              variant="h4"
              fontWeight="700"
              sx={{
                color: 'success.main',
                fontFamily: 'monospace',
                letterSpacing: '-0.5px',
              }}
            >
              {formatPercentage(netReturn)}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.4 }}>
              {isFlipping
                ? 'Incluye todos los costes (impuestos, notaría, etc.)'
                : 'Rentabilidad después de gastos estimados'}
            </Typography>
          </Stack>
        </CardContent>
      </Card>
    </Grid>
  </Grid>
);

const pickReturns = (d: OpportunityFormData) => {
  switch (d.type) {
    case 'TRADITIONAL_RENT':
      return { gross: d.grossReturnRental, net: d.netReturnRental };
    case 'ROOM_RENT':
    case 'ROOM_SALE':
      return { gross: d.grossReturnRooms, net: d.netReturnRooms };
    case 'FLIPPING':
      return { gross: d.grossReturnFlipping, net: d.netReturnFlipping };
    default:
      return { gross: d.grossReturnRental, net: d.netReturnRental };
  }
};

const OpportunityPreview: React.FC<OpportunityPreviewProps> = ({
  data,
  opportunityId,
  mainImageId,
}) => {
  const [center, setCenter] = useState<{ lat: number; lng: number } | null>(null);
  const { isLoaded } = useGoogleMaps();
  const { data: media } = useGetOpportunityMedia(opportunityId);

  const geocodeAddress = data.showApproximateAddress
    ? `${data.province}, España`
    : `${data.address}, ${data.postalCode}, ${data.city}, ${data.province}, España`;

  useEffect(() => {
    if (!isLoaded || !geocodeAddress.trim()) return;

    const fetchCoordinates = async () => {
      try {
        const response = await fetch(
          `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(
            geocodeAddress
          )}&key=${import.meta.env.VITE_GOOGLE_API_KEY ?? ''}`
        );
        const data = await response.json();

        if (data.status === 'OK') {
          const location = data.results[0].geometry.location;
          setCenter({ lat: location.lat, lng: location.lng });
        } else {
          console.error('Error al obtener las coordenadas:', data.status);
        }
      } catch (error) {
        console.error('Error al obtener las coordenadas:', error);
      }
    };

    fetchCoordinates();
  }, [geocodeAddress, isLoaded]);

  const containerStyle = { width: '100%', height: '100%' };

  const normalizePct = (v: number | undefined | null) => {
    if (typeof v !== 'number') return 0;
    return v > 1 ? v / 100 : v;
  };

  const { gross, net } = pickReturns(data);
  const grossReturn = normalizePct(gross);
  const netReturn = normalizePct(net);
  const transactionResults = calculateTransactionResults(data);

  const images =
    media
      ?.filter((m) => m.type === 'IMAGE')
      .map((m) => ({
        url: m.signedUrl,
        mimeType: m.mimeType,
        name: m.fileName,
        isMain: m.id === mainImageId,
      })) ?? [];
  const imageFiles = images.filter((img) => img.mimeType.startsWith('image/'));
  const mainImage = imageFiles.find((img) => img.isMain) ?? imageFiles[0];

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Box style={{ padding: '16px', background: 'white', marginBottom: '16px' }}>
            <Paper elevation={1} style={{ padding: '16px', marginBottom: '16px' }}>
              <Box sx={{ mb: 4 }}>
                {mainImage ? (
                  <Grid container spacing={3} alignItems="flex-start">
                    <Grid item xs={12} md={6}>
                      <Stack spacing={2}>
                        <Box
                          sx={{
                            display: 'inline-block',
                            px: 1.5,
                            py: 0.5,
                            borderRadius: 1,
                            backgroundColor: 'primary.main',
                            color: 'white',
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            textTransform: 'uppercase',
                            letterSpacing: '0.3px',
                            width: 'fit-content',
                          }}
                        >
                          {investmentTypeLabels[data.type]}
                        </Box>

                        {data.type === 'ROOM_SALE' && <RoomSaleAlert />}

                        <Typography variant="h4" fontWeight="bold" sx={{ lineHeight: 1.2 }}>
                          {data.title}
                        </Typography>

                        <Box
                          sx={{
                            color: 'text.secondary',
                            lineHeight: 1.6,
                            display: '-webkit-box',
                            WebkitLineClamp: 15,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                          }}
                        >
                          <RichTextDisplay
                            content={data.description
                              .replace(/&lt;/g, '<')
                              .replace(/&gt;/g, '>')
                              .replace(/&quot;/g, '"')
                              .replace(/&amp;/g, '&')}
                          />
                        </Box>
                      </Stack>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <NoSaveZone selector=".no-save-hero">
                        <Box
                          sx={{
                            position: 'relative',
                            width: '100%',
                            aspectRatio: '4/3',
                            borderRadius: 2,
                            overflow: 'hidden',
                            backgroundColor: '#f5f5f5',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <img
                            src={mainImage.url}
                            alt={mainImage.name}
                            style={{
                              objectFit: 'cover',
                              width: '100%',
                              height: '100%',
                              userSelect: 'none',
                            }}
                            draggable={false}
                          />
                        </Box>
                      </NoSaveZone>
                    </Grid>
                  </Grid>
                ) : (
                  <Stack spacing={2}>
                    <Box
                      sx={{
                        display: 'inline-block',
                        px: 1.5,
                        py: 0.5,
                        borderRadius: 1,
                        backgroundColor: 'primary.main',
                        color: 'white',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '0.3px',
                        width: 'fit-content',
                      }}
                    >
                      {investmentTypeLabels[data.type]}
                    </Box>

                    {data.type === 'ROOM_SALE' && <RoomSaleAlert />}

                    <Typography variant="h4" fontWeight="bold">
                      {data.title}
                    </Typography>

                    <Box
                      sx={{
                        color: 'text.secondary',
                        lineHeight: 1.6,
                      }}
                    >
                      <RichTextDisplay
                        content={data.description
                          .replace(/&lt;/g, '<')
                          .replace(/&gt;/g, '>')
                          .replace(/&quot;/g, '"')
                          .replace(/&amp;/g, '&')}
                      />
                    </Box>
                  </Stack>
                )}
              </Box>
            </Paper>

            {/* Return Metrics - Prominent Display in Separate Card */}
            <Paper elevation={1} style={{ padding: '16px', marginBottom: '16px' }}>
              <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                Rentabilidad Proyectada
              </Typography>
              <ReturnMetricsCards
                grossReturn={grossReturn}
                netReturn={netReturn}
                isFlipping={data.type === 'FLIPPING'}
              />
            </Paper>

            <Paper elevation={1} style={{ padding: '16px', marginBottom: '16px' }}>
              <Typography variant="h6">Ubicación:</Typography>
              <Typography variant="body1">
                {data.showApproximateAddress
                  ? `Provincia: ${data.province}`
                  : `${data.address}, ${data.postalCode} - ${data.city}, ${data.province}`}
              </Typography>
              {data.showApproximateAddress && (
                <Typography variant="body2" color="text.secondary">
                  El PSI ha decidido no indicar la localización exacta. Se compartirá al avanzar en
                  el proceso de inversión.
                </Typography>
              )}
              <Typography variant="body1">
                Sobre la ubicación: {data.locationDescription}
              </Typography>
              {!data.showApproximateAddress && (
                <Box flex={1} marginRight="8px" height={'200px'}>
                  {isLoaded && center ? (
                    <GoogleMap mapContainerStyle={containerStyle} center={center} zoom={MAP_ZOOM}>
                      <Marker position={center} />
                    </GoogleMap>
                  ) : (
                    <Typography variant="body1">Cargando mapa...</Typography>
                  )}
                </Box>
              )}
            </Paper>

            <Paper elevation={1} style={{ padding: '16px', marginBottom: '16px' }}>
              <Typography variant="h6">Aspectos generales y características:</Typography>
              <Grid container>
                {[
                  { label: 'Referencia catastral', value: data.cadastralReference },
                  { label: 'Vendedores', value: data.sellers },
                  {
                    label: 'ITE pasada',
                    value: `${getTernaryLabel(data.itePassed)} - ${data.itePassedObservations}`,
                  },
                  { label: 'Orientación vivienda', value: data.houseOrientation },
                  { label: 'Año construcción', value: data.buildingYear },
                  { label: 'Planta', value: data.buildingFloor },
                  { label: 'Habitaciones', value: data.rooms },
                  { label: 'Baños', value: data.bathrooms },
                  { label: 'Metros cuadrados', value: formatDecimal(data.squareMeters, 2) },
                ].map((item, index) => (
                  <React.Fragment key={index}>
                    <Grid
                      item
                      xs={4}
                      style={{ backgroundColor: getBackgroundColor(index), padding: '8px' }}
                    >
                      <Typography variant="body1">{item.label}</Typography>
                    </Grid>
                    <Grid
                      item
                      xs={8}
                      style={{ backgroundColor: getBackgroundColor(index), padding: '8px' }}
                    >
                      <Typography variant="body1">{item.value}</Typography>
                    </Grid>
                  </React.Fragment>
                ))}
              </Grid>
            </Paper>

            <Paper elevation={1} style={{ padding: '16px', marginBottom: '16px' }}>
              <Typography variant="h6">Conservación:</Typography>
              <Grid container>
                {[
                  {
                    label: 'Electricidad',
                    value: <Rating value={data.electricityCondition} readOnly />,
                    name: 'read-only-1',
                  },
                  {
                    label: 'Fontanería',
                    value: <Rating value={data.plumbingCondition} readOnly />,
                    name: 'read-only-2',
                  },
                  {
                    label: 'Calle',
                    value: <Rating value={data.streetCondition} readOnly />,
                    name: 'read-only-3',
                  },
                  {
                    label: 'Fachada',
                    value: <Rating value={data.facadeCondition} readOnly />,
                    name: 'read-only-4',
                  },
                  {
                    label: 'Postigo',
                    value:
                      data.shuttersCondition !== null ? (
                        <Rating value={data.shuttersCondition} readOnly />
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No aplica
                        </Typography>
                      ),
                    name: 'read-only-5',
                  },
                  {
                    label: 'Escalera',
                    value:
                      data.stairCondition !== null ? (
                        <Rating value={data.stairCondition} readOnly />
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No aplica
                        </Typography>
                      ),
                    name: 'read-only-6',
                  },
                  {
                    label: 'Terraza',
                    value:
                      data.terraceCondition !== null ? (
                        <Rating value={data.terraceCondition} readOnly />
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No aplica
                        </Typography>
                      ),
                    name: 'read-only-7',
                  },
                ].map((item, index) => (
                  <React.Fragment key={item.name}>
                    <Grid
                      item
                      xs={4}
                      style={{ backgroundColor: getBackgroundColor(index), padding: '8px' }}
                    >
                      <Typography variant="body1">{item.label}</Typography>
                    </Grid>
                    <Grid
                      item
                      xs={8}
                      style={{
                        backgroundColor: getBackgroundColor(index),
                        padding: '8px',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      {item.value}
                    </Grid>
                  </React.Fragment>
                ))}
              </Grid>
            </Paper>

            <Paper elevation={1} style={{ padding: '16px', marginBottom: '16px' }}>
              <Typography variant="h6" gutterBottom>
                Checklist:
              </Typography>
              <Grid container>
                {[
                  {
                    label: 'Alta luz',
                    value: data.electricityRegistration,
                    details: data.electricityRegistrationObservations,
                  },
                  {
                    label: 'Alta agua',
                    value: data.waterRegistration,
                    details: data.waterRegistrationObservations,
                  },
                  {
                    label: 'Se vende vacío',
                    value: data.vacantSale,
                    details: data.vacantSaleObservations,
                  },
                  {
                    label: 'Ascensor',
                    value: data.hasElevator,
                    details: data.hasElevatorObservations,
                  },
                  {
                    label: 'Certificado Eficiencia Energética',
                    value: data.energyCertificate,
                    details: data.energyCertificateObservations,
                  },
                  {
                    label: 'Certificado Deuda',
                    value: data.debtCertificate,
                    details: data.debtCertificateObservations,
                  },
                  {
                    label: 'Nota simple',
                    value: data.simpleCopy,
                    details: data.simpleCopyObservations,
                  },
                  { label: 'VPO', value: data.vpo, details: data.vpoObservations },
                  {
                    label: 'Proviene de herencia',
                    value: data.inheritance,
                    details: data.inheritanceObservations,
                  },
                ].map((item, index) => (
                  <React.Fragment key={index}>
                    <Grid
                      item
                      xs={4}
                      style={{ backgroundColor: getBackgroundColor(index), padding: '8px' }}
                    >
                      <Typography variant="body1">{item.label}</Typography>
                    </Grid>
                    <Grid
                      item
                      xs={8}
                      style={{
                        backgroundColor: getBackgroundColor(index),
                        padding: '8px',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      {getTernaryOptionIcon(item.value as TernaryOption)}
                      <Typography variant="body1" sx={{ marginLeft: '8px' }}>
                        {item.details}
                      </Typography>
                    </Grid>
                  </React.Fragment>
                ))}
              </Grid>
            </Paper>

            <Paper elevation={1} style={{ padding: '16px', marginBottom: '16px' }}>
              <Typography variant="h6">Gastos anuales:</Typography>
              <Grid container>
                {[
                  {
                    label: 'Cuota comunidad',
                    value: data.communityFee ? formatCurrency(data.communityFee * 12) : '-',
                  },
                  {
                    label: 'IBI',
                    value: data.propertyTaxIbi ? formatCurrency(data.propertyTaxIbi) : '-',
                  },
                  {
                    label: 'Seguro hogar',
                    value: data.homeInsurance ? formatCurrency(data.homeInsurance) : '-',
                  },
                  {
                    label: 'Seguro impago',
                    value: data.rentDefaultInsurance
                      ? formatCurrency(data.rentDefaultInsurance)
                      : '-',
                  },
                  {
                    label: 'Tasa basuras',
                    value: data.wasteTax ? formatCurrency(data.wasteTax) : '-',
                  },
                  {
                    label: 'Otros gastos',
                    value: data.otherExpenses ? formatCurrency(data.otherExpenses) : '-',
                  },
                ].map((item, index) => (
                  <React.Fragment key={index}>
                    <Grid
                      item
                      xs={4}
                      style={{ backgroundColor: getBackgroundColor(index), padding: '8px' }}
                    >
                      <Typography variant="body1">{item.label}</Typography>
                    </Grid>
                    <Grid
                      item
                      xs={8}
                      style={{
                        backgroundColor: getBackgroundColor(index),
                        padding: '8px',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      {item.value}
                    </Grid>
                  </React.Fragment>
                ))}
              </Grid>
            </Paper>

            <Paper elevation={1} style={{ padding: '16px', marginBottom: '16px' }}>
              <Typography variant="h6" gutterBottom>
                Datos económicos:
              </Typography>
              <Grid container>
                {[
                  {
                    label: 'Precio de compra:',
                    value: formatCurrency(data.negotiatedPrice ?? undefined),
                  },
                  {
                    label: 'Cantidad a escriturar:',
                    value: formatCurrency(data.deedAmount ?? undefined),
                  },
                  {
                    label: 'Valor de referencia:',
                    value: formatCurrency(data.referenceValue ?? undefined),
                  },
                  { label: 'Impuestos:', value: formatCurrency(data.purchaseTaxItp ?? undefined) },
                  {
                    label: 'Gastos notaría:',
                    value: formatCurrency(data.notaryExpenses ?? undefined),
                  },
                  {
                    label: 'Gastos registro:',
                    value: formatCurrency(data.registryExpenses ?? undefined),
                  },
                  {
                    label: 'Otros gastos compra:',
                    value: formatCurrency(data.purchaseExpenses ?? undefined),
                  },
                  {
                    label: 'Comisión PSI:',
                    value: formatCurrency(data.psiCommission ?? undefined),
                  },
                  {
                    label: 'Comisión inmobiliaria:',
                    value: formatCurrency(data.realtorCommission ?? undefined),
                  },
                  {
                    label: 'Reforma estimada:',
                    value: formatCurrency(data.renovationCost ?? undefined),
                  },
                  // FLIPPING fields
                  ...(data.type === 'FLIPPING'
                    ? [
                        {
                          label: 'Precio de venta estimado:',
                          value: formatCurrency(data.estimatedSalePrice ?? undefined),
                        },
                        {
                          label: 'Ganancia estimada:',
                          value: formatCurrency(
                            (data.estimatedSalePrice ?? 0) - transactionResults.totalExpenses
                          ),
                        },
                      ]
                    : [
                        {
                          label: 'Alquiler mensual esperado:',
                          value: formatCurrency(
                            data.type === 'ROOM_RENT'
                              ? (data.roomRent ?? 0) * data.rooms
                              : (data.expectedMonthlyRent ?? 0)
                          ),
                        },
                      ]),
                  {
                    label: 'Rentabilidad bruta:',
                    value: formatPercentage(grossReturn),
                  },
                  {
                    label: 'Rentabilidad neta:',
                    value: formatPercentage(netReturn),
                  },
                ].map((item, index) => (
                  <React.Fragment key={index}>
                    <Grid
                      item
                      xs={4}
                      style={{ backgroundColor: getBackgroundColor(index), padding: '8px' }}
                    >
                      <Typography variant="body1">{item.label}</Typography>
                    </Grid>
                    <Grid
                      item
                      xs={8}
                      style={{
                        backgroundColor: getBackgroundColor(index),
                        padding: '8px',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      {item.value}
                    </Grid>
                  </React.Fragment>
                ))}
              </Grid>
            </Paper>

            <Paper elevation={1} style={{ padding: '16px' }}>
              <Typography variant="h6">Observaciones:</Typography>
              <Typography variant="body1">{data.observations}</Typography>
            </Paper>

            <br />
            <Paper elevation={1} style={{ padding: '16px', overflow: 'hidden' }}>
              <Typography variant="h6">Galería:</Typography>

              <NoSaveZone selector=".no-save-gallery">
                <Box
                  sx={{
                    overflow: 'hidden',
                  }}
                >
                  <ImageGallery files={images} />
                </Box>
              </NoSaveZone>
            </Paper>

            <br />
            <Paper
              elevation={1}
              style={{
                padding: '16px',
                backgroundColor: '#fff3cd',
                borderLeft: '4px solid #ffc107',
              }}
            >
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                <strong>Aviso importante:</strong> InversorHub no se hace responsable del contenido
                que el PSI introduce en las oportunidades. El inversor debe realizar su propia
                comprobación y confirmar los datos con el PSI antes de formalizar cualquier
                operación.
              </Typography>
            </Paper>
          </Box>
        </Grid>
      </Grid>
    </>
  );
};

export default OpportunityPreview;
